import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Layers,
  FileText,
  RefreshCw,
  GitBranch,
  Workflow,
  Receipt,
  ShieldCheck,
  Award,
  Users,
  BarChart3,
  Settings2,
  AlertTriangle,
  TrendingDown,
  ArrowDown,
} from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

/* ───────────────────────────── Data ───────────────────────────── */
const challenges = [
  "Complex multi-system operations",
  "Long policy issuance turnaround times",
  "Heavy operational dependency for renewals",
  "Slow batch processing and reconciliation",
  "Fragmented receipting and servicing workflows",
  "Limited operational visibility and reporting",
];

const metrics = [
  {
    value: "73%",
    label: "Reduction in New Business Processing Time",
    detail: "Average processing time reduced from 75 minutes to 20 minutes per policy.",
    Icon: FileText,
  },
  {
    value: "68%",
    label: "Reduction in Renewal Processing Time",
    detail: null,
    Icon: RefreshCw,
  },
  {
    value: "98%",
    label: "Reduction in Micro Insurance Processing Time",
    detail: null,
    Icon: Sparkles,
  },
  {
    value: "47 → 7",
    label: "Reduction in Operational Screens",
    detail: "Significantly simplified operational journeys and servicing flows.",
    Icon: Layers,
  },
];

const impactBlocks = [
  {
    Icon: GitBranch,
    title: "System Consolidation",
    description:
      "Unified receipting, policy administration, and certificate issuance into a single operational platform.",
  },
  {
    Icon: Workflow,
    title: "Batch Processing Optimization",
    description:
      "Reduced day-end and month-end processing cycles from several hours to under one hour.",
  },
  {
    Icon: RefreshCw,
    title: "Renewal Workflow Automation",
    description:
      "Standardized early renewal exception handling and reduced renewal turnaround from multiple days to under one hour.",
  },
  {
    Icon: BarChart3,
    title: "Operational Intelligence",
    description:
      "Enabled sales hierarchy visibility, intermediary mapping, operational reporting, and configurable accounting controls.",
  },
];

const capabilities = [
  { Icon: Users, label: "Group Insurance" },
  { Icon: FileText, label: "Policy Administration" },
  { Icon: Receipt, label: "Receipting" },
  { Icon: Award, label: "Certificate Issuance" },
  { Icon: RefreshCw, label: "Renewals" },
  { Icon: Workflow, label: "Workflow Automation" },
  { Icon: BarChart3, label: "Operational Reporting" },
  { Icon: Settings2, label: "Accounting Controls" },
  { Icon: Users, label: "Intermediary Management" },
];

/* ───────────────────────────── Page ───────────────────────────── */
export default function MFInsureCoreCaseStudy() {
  return (
    <main className="bg-white text-ink" data-testid="mfinsure-core-case-study">
      <SEO
        title="Case Study — Transforming Group Insurance Operations with MFInsure® Core"
        description="A leading Indian life insurer modernized group insurance operations with Gradatim MFInsure® Core — 73% faster new business, 68% faster renewals, 47 → 7 operational screens."
        path="/insights/mfinsure-core"
      />
      <Nav />

      {/* HERO */}
      <section className="relative bg-white pt-32 md:pt-40 pb-12 md:pb-16 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.4]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(11,31,58,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,31,58,0.04) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.6), transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.6), transparent 80%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-copper/20 bg-copper/5"
            data-testid="case-study-badge"
          >
            <Sparkles className="h-3.5 w-3.5 text-copper" strokeWidth={2} />
            <span className="overline text-copper">Operational Transformation at Scale</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-4xl sm:text-5xl md:text-[56px] lg:text-[60px] tracking-tight leading-[1.05] text-ink mt-8 max-w-[1100px] mx-auto"
            data-testid="case-study-title"
          >
            Transforming Group Insurance Operations
            <br className="hidden md:block" />{" "}
            with <span className="text-copper">Gradatim MFInsure® Core.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="text-base md:text-[18px] text-slate-600 leading-relaxed mt-8 max-w-[840px] mx-auto"
          >
            Gradatim MFInsure® Core enabled one of India's leading life insurers
            to modernize and consolidate complex group insurance operations
            across policy administration, receipting, renewals, accounting,
            workflows, and digital servicing.
          </motion.p>
        </div>
      </section>

      {/* CUSTOMER CONTEXT */}
      <section className="bg-cream border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5"
              data-testid="customer-context-block"
            >
              <div className="overline text-copper mb-4">Customer Context</div>
              <h2 className="font-display text-2xl md:text-[28px] lg:text-[32px] tracking-tight leading-[1.15] text-ink">
                A leading Indian life insurer.
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7 lg:pt-2"
            >
              <p className="text-base md:text-[17px] text-slate-600 leading-relaxed">
                The insurer operated large-scale group life, group credit life,
                annuity, and fund products across multiple operational systems
                with high manual dependency, long processing cycles, and
                fragmented workflows.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CHALLENGE → SOLUTION */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Challenge */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl bg-white p-7 md:p-9 border border-hairline"
              style={{ boxShadow: "0 8px 28px -12px rgba(11,31,58,0.10)" }}
              data-testid="challenge-block"
            >
              <div className="flex items-center gap-3 mb-7">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-copper/10 border border-copper/20">
                  <AlertTriangle className="h-4.5 w-4.5 text-copper" strokeWidth={1.8} />
                </span>
                <div>
                  <div className="overline text-slate-500">The Challenge</div>
                  <h3 className="font-display text-xl tracking-tight text-ink">
                    Operational Challenges
                  </h3>
                </div>
              </div>
              <ul className="space-y-3.5">
                {challenges.map((c) => (
                  <li
                    key={c}
                    className="flex items-start gap-3 pb-3.5 border-b last:border-0 last:pb-0"
                    style={{ borderColor: "rgba(11,31,58,0.06)" }}
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-copper shrink-0" />
                    <span className="text-[14.5px] text-slate-600 leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Solution */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl p-7 md:p-9 overflow-hidden grain-overlay"
              style={{
                background: "#0B1F3A",
                boxShadow: "0 20px 60px -20px rgba(11,31,58,0.40)",
              }}
              data-testid="solution-block"
            >
              <div
                aria-hidden
                className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-copper/15 blur-3xl"
              />
              <div className="relative flex items-center gap-3 mb-7">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-copper/20 border border-copper/30">
                  <ShieldCheck className="h-4.5 w-4.5 text-copper" strokeWidth={1.8} />
                </span>
                <div>
                  <div className="overline text-copper">The Solution</div>
                  <h3 className="font-display text-xl tracking-tight text-white">
                    Gradatim Transformation
                  </h3>
                </div>
              </div>
              <p className="relative text-[15px] md:text-base leading-relaxed text-slate-300">
                Gradatim MFInsure® Core unified policy administration,
                receipting, certificate issuance, accounting, workflow
                orchestration, renewals, intermediary structures, and operational
                reporting into a single configurable insurance operating
                platform.
              </p>
              <div className="relative mt-7 grid grid-cols-3 gap-3">
                {["Unified", "Configurable", "Operational"].map((tag) => (
                  <div
                    key={tag}
                    className="text-center py-2.5 rounded-md border border-white/10 bg-white/5 backdrop-blur-sm"
                  >
                    <span className="text-[11.5px] uppercase tracking-[0.2em] text-white/80">
                      {tag}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Down arrow connecting to metrics */}
          <div className="flex justify-center mt-12 md:mt-14">
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6 }}
              className="inline-flex flex-col items-center gap-2"
            >
              <span className="overline text-copper">The Outcome</span>
              <ArrowDown className="h-5 w-5 text-copper animate-bounce" strokeWidth={1.8} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* HERO METRICS */}
      <section className="relative bg-cream border-y border-hairline overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(200,75,49,0.08), transparent 70%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-medium text-3xl sm:text-4xl md:text-[48px] tracking-tight leading-[1.05] text-ink"
              data-testid="metrics-headline"
            >
              Outcomes delivered, <span className="text-copper">measurable at scale.</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-testid="metric-cards">
            {metrics.map((m, i) => (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.05 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -4 }}
                className="relative rounded-2xl bg-white p-7 md:p-8 border border-hairline transition-shadow hover:shadow-lg"
                style={{ boxShadow: "0 4px 20px -8px rgba(11,31,58,0.08)" }}
                data-testid={`metric-card-${i + 1}`}
              >
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-copper/10 border border-copper/20 mb-6">
                  <m.Icon className="h-4.5 w-4.5 text-copper" strokeWidth={1.8} />
                </div>
                <div className="font-display font-medium text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-none text-ink">
                  {m.value}
                </div>
                <div className="mt-4 text-[13.5px] md:text-[14px] text-slate-700 font-medium leading-snug">
                  {m.label}
                </div>
                {m.detail && (
                  <div className="mt-4 pt-4 border-t border-hairline">
                    <p className="text-[12.5px] text-slate-500 leading-relaxed">
                      {m.detail}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 md:mt-14 flex items-center justify-center gap-2.5"
          >
            <TrendingDown className="h-4 w-4 text-copper" strokeWidth={2} />
            <span className="text-[13px] text-slate-600">
              Significant reductions across new business, renewals, micro insurance and operational complexity.
            </span>
          </motion.div>
        </div>
      </section>

      {/* OPERATIONAL IMPACT */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-copper/20 bg-copper/5"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-copper" />
              <span className="overline text-copper">Operational Impact</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] tracking-tight leading-[1.08] text-ink mt-6"
              data-testid="impact-headline"
            >
              Operational impact, <span className="text-copper">delivered.</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" data-testid="impact-blocks">
            {impactBlocks.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.05 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -3 }}
                className="group relative rounded-2xl bg-cream p-7 md:p-8 border border-hairline transition-all hover:bg-white hover:shadow-md"
                data-testid={`impact-block-${i + 1}`}
              >
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(200,75,49,0.6) 50%, transparent 100%)",
                  }}
                />
                <div className="flex items-start gap-5">
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-white border border-hairline shrink-0 group-hover:border-copper/40 transition-colors">
                    <b.Icon className="h-5 w-5 text-copper" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-display text-[20px] md:text-[22px] tracking-tight text-ink mb-3 leading-tight">
                      {b.title}
                    </h3>
                    <p className="text-[14px] md:text-[14.5px] text-slate-600 leading-relaxed">
                      {b.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITY STRIP */}
      <section className="bg-cream border-t border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="flex items-center justify-center gap-3 mb-9">
            <span className="h-px w-12 bg-hairline" />
            <span className="overline text-slate-500">Enterprise Capabilities</span>
            <span className="h-px w-12 bg-hairline" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3" data-testid="capability-strip">
            {capabilities.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: 0.03 + i * 0.03, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -2 }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-hairline hover:border-copper/40 hover:shadow-sm transition-all"
              >
                <c.Icon className="h-3.5 w-3.5 text-copper" strokeWidth={1.8} />
                <span className="text-[12.5px] font-medium tracking-tight text-ink">
                  {c.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[24px] md:rounded-[32px] overflow-hidden grain-overlay px-8 md:px-14 py-14 md:py-20 text-center"
            style={{ background: "#0B1F3A" }}
            data-testid="closing-cta-block"
          >
            <div aria-hidden className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-copper/15 blur-3xl" />
            <div aria-hidden className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.05] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "60px 60px",
              }}
            />
            <div className="relative">
              <span className="overline text-copper">Ready to transform?</span>
              <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-[52px] tracking-tight leading-[1.05] text-white mt-5 max-w-[820px] mx-auto">
                Modernize your group insurance{" "}
                <span className="text-copper">operations with Gradatim.</span>
              </h2>
              <p className="text-[15px] md:text-base text-slate-300 leading-relaxed mt-7 max-w-[640px] mx-auto">
                Talk to our team about consolidating your policy administration,
                renewals, accounting and workflows into one configurable platform.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link
                  to="/#contact"
                  data-testid="closing-cta-primary"
                  className="group inline-flex items-center gap-3 bg-copper hover:bg-copper-600 text-white px-7 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
                >
                  Request a demo
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/products/core"
                  data-testid="closing-cta-secondary"
                  className="group inline-flex items-center gap-3 border border-white/30 hover:border-copper text-white hover:text-copper px-7 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
                >
                  Explore MFInsure® Core
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
